import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, MessageSquare, Phone, Search, Voicemail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CommunicationItem, { CommunicationItemFilter, CommunicationItemKind } from "@/model/interface/CommunicationItem";
import { useGetAttentionCountQuery, useGetCommunicationItemsInfiniteQuery } from "@/store/rest_api";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import InboxFeedRow from "./InboxFeedRow";

type FeedChannel = "all" | "texts" | "calls" | "voicemail";
type FeedFocus = "none" | "unread" | "missed" | "flagged";

interface InboxFeedProps {
  archived?: boolean;
  initialChannel?: FeedChannel;
  selectedConversationId?: string;
  selectedContactId?: string;
  highlightedConversationId?: string | null;
  onOpen: (item: CommunicationItem, searchResult: boolean) => void;
}

const CHANNEL_KINDS: Record<FeedChannel, CommunicationItemKind[] | undefined> = {
  all: undefined,
  texts: ["TEXT"],
  calls: ["CALL"],
  voicemail: ["VOICEMAIL"],
};

const FOCUS_OPTIONS: { value: FeedFocus; label: string }[] = [
  { value: "unread", label: "Unread" },
  { value: "missed", label: "Missed" },
  { value: "flagged", label: "Flagged" },
];

function ChannelCount({ count }: { count?: number }) {
  if (!count) {
    return null;
  }
  return (
    <span className="ml-1.5 rounded-full bg-foreground/10 px-1.5 text-[10px] font-medium tabular-nums">
      {count > 99 ? "99+" : count}
    </span>
  );
}

const LOAD_MORE_THRESHOLD_PX = 160;
const SEARCH_DEBOUNCE_MS = 300;

const InboxFeed: React.FC<InboxFeedProps> = ({
  archived = false,
  initialChannel = "all",
  selectedConversationId,
  selectedContactId,
  highlightedConversationId,
  onOpen,
}) => {
  const [channel, setChannel] = useState<FeedChannel>(initialChannel);
  const { data: attention } = useGetAttentionCountQuery();
  const [focus, setFocus] = useState<FeedFocus>("none");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const searching = debouncedSearch.length > 0;
  const scrollRef = useRef<HTMLDivElement>(null);
  const filter = useMemo<CommunicationItemFilter>(
    () => ({
      grouped: !searching,
      archived: archived || undefined,
      q: searching ? debouncedSearch : undefined,
      kind: focus === "missed" ? ["CALL"] : CHANNEL_KINDS[channel],
      needsAttention: focus === "unread" || focus === "missed" ? true : undefined,
      flagged: focus === "flagged" ? true : undefined,
    }),
    [archived, channel, focus, searching, debouncedSearch],
  );
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useGetCommunicationItemsInfiniteQuery(filter);
  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  const toggleFocus = (value: FeedFocus) => {
    setFocus((current) => (current === value ? "none" : value));
  };

  const handleScroll = () => {
    const element = scrollRef.current;
    if (!element || !hasNextPage || isFetchingNextPage) {
      return;
    }
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (distanceFromBottom < LOAD_MORE_THRESHOLD_PX) {
      void fetchNextPage();
    }
  };

  useEffect(() => {
    setChannel(initialChannel);
  }, [initialChannel]);

  const emptyCopy = () => {
    if (searching) {
      return "Nothing matches that search.";
    }
    if (archived) {
      return "No archived conversations.";
    }
    if (focus === "unread") {
      return "Nothing needs your attention.";
    }
    if (focus === "missed") {
      return "No missed calls waiting for a callback.";
    }
    if (focus === "flagged") {
      return "No flagged conversations.";
    }
    if (channel === "voicemail") {
      return "No voicemails yet.";
    }
    if (channel === "calls") {
      return "No calls yet.";
    }
    return "No conversations yet. Start one with the button above.";
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="space-y-2 border-b border-border p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={archived ? "Search archived" : "Search texts, calls and contacts"}
            className="pl-9"
          />
        </div>
        <Tabs value={channel} onValueChange={(value) => setChannel(value as FeedChannel)}>
          <TabsList className="w-full">
            <TabsTrigger value="all" className="px-2.5">
              All
              <ChannelCount count={attention?.total} />
            </TabsTrigger>
            <TabsTrigger value="texts" className="flex-1" title="Texts">
              <MessageSquare className="h-4 w-4" />
              <ChannelCount count={attention?.texts} />
            </TabsTrigger>
            <TabsTrigger value="calls" className="flex-1" title="Calls">
              <Phone className="h-4 w-4" />
              <ChannelCount count={attention?.calls} />
            </TabsTrigger>
            <TabsTrigger value="voicemail" className="flex-1" title="Voicemail">
              <Voicemail className="h-4 w-4" />
              <ChannelCount count={attention?.voicemails} />
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {!archived && (
          <div className="grid grid-cols-3 gap-2">
            {FOCUS_OPTIONS.map((option) => (
              <Button
                key={option.value}
                variant={focus === option.value ? "secondary" : "outline"}
                size="sm"
                className="h-9 w-full text-sm"
                onClick={() => toggleFocus(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        )}
      </div>
      <div ref={scrollRef} onScroll={handleScroll} className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && items.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">{emptyCopy()}</div>
        )}
        {items.map((item) => (
          <InboxFeedRow
            key={item.id}
            item={item}
            archived={archived}
            searchResult={searching}
            selected={
              !searching &&
              ((!!item.conversationId && item.conversationId === selectedConversationId) ||
                (!!item.contact?.id && item.contact.id === selectedContactId))
            }
            highlighted={!searching && !!item.conversationId && item.conversationId === highlightedConversationId}
            onOpen={(opened) => onOpen(opened, searching)}
          />
        ))}
        {isFetchingNextPage && (
          <div className="flex justify-center py-3">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    </div>
  );
};

export default InboxFeed;
